<template>
  <header class="cbz-topbar">
    <div v-if="loading" class="cbz-topbar__status">Cargando...</div>

    <template v-else-if="park">
      <div class="cbz-topbar__left">
        <span class="cbz-topbar__section">Cobranza</span>
        <span class="cbz-topbar__park-name">{{ park.name }}</span>
      </div>

      <div class="cbz-topbar__divider"></div>

      <div class="cbz-topbar__period-group">
        <div class="cbz-topbar__period-wrap">
          <button class="cbz-topbar__period-select" @click="periodOpen = !periodOpen">
            {{ currentLabel }} <span class="mdi mdi-chevron-down"></span>
          </button>
          <div v-if="periodOpen" class="cbz-topbar__period-menu">
            <button v-for="p in periods" :key="p.value" class="cbz-topbar__period-item" :class="{ 'cbz-topbar__period-item--active': p.value === modelValue }" @click="selectPeriod(p)">
              {{ p.label }}
            </button>
          </div>
        </div>

        <label class="cbz-topbar__accumulated">
          <input type="checkbox" :checked="accumulated" @change="$emit('update:accumulated', $event.target.checked)" />
          Ver acumulado
        </label>
      </div>


      <div class="cbz-topbar__divider"></div>

      <div class="cbz-topbar__search-wrap">
        <div class="cbz-topbar__search" :class="{ 'cbz-topbar__search--active': searchActive }">
          <span class="mdi mdi-magnify cbz-topbar__search-icon"></span>
          <input
            ref="searchInputEl"
            :value="search"
            type="text"
            class="cbz-topbar__search-input"
            placeholder="Buscar inquilino o cargo..."
            @focus="searchFocused = true"
            @blur="onSearchBlur"
            @input="$emit('update:search', $event.target.value)"
          />
          <button v-if="search" class="cbz-topbar__search-clear" tabindex="-1" @mousedown.prevent="clearSearch">
            <span class="mdi mdi-close"></span>
          </button>
        </div>

        <div v-if="showDropdown" class="cbz-topbar__dropdown">
          <div v-if="searchResults.tenants?.length" class="cbz-topbar__dropdown-group">
            <div class="cbz-topbar__dropdown-label">Inquilinos</div>
            <button v-for="t in searchResults.tenants" :key="t.contractId" class="cbz-topbar__dropdown-item" @mousedown.prevent="selectResult('tenant', t)">
              <span class="cbz-topbar__dropdown-item-name">{{ t.name }}</span>
            </button>
          </div>
          <div v-if="searchResults.charges?.length" class="cbz-topbar__dropdown-group">
            <div class="cbz-topbar__dropdown-label">Cargos</div>
            <button v-for="c in searchResults.charges" :key="c.id" class="cbz-topbar__dropdown-item" @mousedown.prevent="selectResult('charge', c)">
              <span class="cbz-topbar__dropdown-item-name">{{ c.concept }}</span>
              <span class="cbz-topbar__dropdown-item-context">{{ c.tenantName }} · ${{ c.amount }}</span>
            </button>
          </div>
          <div v-if="!searchResults.tenants?.length && !searchResults.charges?.length" class="cbz-topbar__dropdown-empty">Sin resultados</div>
        </div>
      </div>

      <div class="cbz-topbar__spacer"></div>

      <div class="cbz-topbar__statement-wrap">
        <button class="cbz-topbar__statement-btn" @mouseenter="statementTooltip = true" @mouseleave="statementTooltip = false" @click="$emit('generate-statements')">
          <template v-if="generatingProgress">Generando {{ generatingProgress.done }} de {{ generatingProgress.total }}...</template>
          <template v-else>Generar estados de cuenta</template>
        </button>
        <div v-if="statementTooltip && !generatingProgress" class="cbz-topbar__tooltip cbz-topbar__tooltip--right">{{ statementTooltipText }}</div>
      </div>

      <div class="cbz-topbar__divider"></div>

      <GlobalAlertsChip :park-id="park?.id" />
      <NotificationBell :park-id="park?.id" />
    </template>
  </header>

<div v-if="park" class="cbz-statusbar">
  <div class="cbz-statusbar__tabs">
    <button
      v-for="tab in TABS"
      :key="tab.key"
      class="cbz-statusbar__tab"
      :class="[`cbz-statusbar__tab--${tab.key}`, { 'cbz-statusbar__tab--active': activeTab === tab.key }]"
      @click="$emit('update:activeTab', tab.key)"
    >
      <span v-if="tab.key === 'vencidos' && counts.vencidos > 0" class="cbz-statusbar__tab-blink"></span>
      {{ tab.label }} <span class="cbz-statusbar__tab-count" :class="`cbz-statusbar__tab-count--${tab.key}`">({{ counts[tab.countKey] ?? 0 }})</span>
    </button>
  </div>

  <div class="cbz-statusbar__new-charge-wrap">
    <button class="cbz-statusbar__new-charge-btn" @mouseenter="newChargeTooltip = true" @mouseleave="newChargeTooltip = false" @click="$emit('new-charge')">
      ＋ Nuevo cargo
    </button>
    <div v-if="newChargeTooltip" class="cbz-statusbar__tooltip">La renta, servicios, y mantenimiento se generan automáticamente. Usa este botón solo para cargos adicionales.</div>
  </div>
</div>
  
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import NotificationBell from '@/components/Global/NotificationBell.vue'
import GlobalAlertsChip from '@/components/Global/GlobalAlertsChip.vue'
import { getChargePeriods, getChargeCounts, searchCharges } from '@/services/charges'

const props = defineProps({
  park: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] },
  modelValue: { type: String, default: '' },
  accumulated: { type: Boolean, default: false },
  activeTab: { type: String, default: 'pendientes' },
  search: { type: String, default: '' },
  selectedCount: { type: Number, default: 0 },
  generatingProgress: { type: Object, default: null },
})

const emit = defineEmits([
  'update:modelValue', 'update:accumulated', 'update:activeTab', 'update:search',
  'new-charge', 'generate-statements', 'open-notifications',
])

const TABS = [
  { key: 'pendientes', label: 'Pendientes', countKey: 'pendientes' },
  { key: 'vencidos', label: 'Vencidos', countKey: 'vencidos' },
  { key: 'alCorriente', label: 'Al corriente', countKey: 'alCorriente' },
  { key: 'todos', label: 'Todos', countKey: 'todos' },
]

const periods = ref([])
const counts = ref({ todos: 0, pendientes: 0, alCorriente: 0, vencidos: 0 })
const periodOpen = ref(false)
const newChargeTooltip = ref(false)
const statementTooltip = ref(false)

async function loadPeriods() {
  if (!props.park) return
  periods.value = await getChargePeriods(props.park.id)
  if (!props.modelValue && periods.value.length) {
    emit('update:modelValue', periods.value[periods.value.length - 1].value)
  }
}

async function loadCounts() {
  if (!props.park || !props.modelValue) return
  counts.value = await getChargeCounts(props.park.id, props.modelValue, props.accumulated)
}

watch(() => props.park, loadPeriods, { immediate: true })
watch([() => props.modelValue, () => props.accumulated], loadCounts, { immediate: true })

const currentLabel = computed(() => periods.value.find((p) => p.value === props.modelValue)?.label || '—')

function selectPeriod(p) {
  periodOpen.value = false
  emit('update:modelValue', p.value)
}

const statementTooltipText = computed(() => {
  if (props.selectedCount > 0) return `Generará estados de cuenta solo para los ${props.selectedCount} inquilinos seleccionados.`
  if (props.activeTab !== 'todos') return `Generará estados de cuenta solo para los ${counts.value[props.activeTab] ?? 0} inquilinos filtrados actualmente.`
  return ''
})

// --- Buscador ---
const searchInputEl = ref(null)
const searchFocused = ref(false)
const searchResults = ref({ tenants: [], charges: [] })
let debounceHandle = null

watch(() => props.search, (q) => {
  if (debounceHandle) clearTimeout(debounceHandle)
  if (!q.trim()) { searchResults.value = { tenants: [], charges: [] }; return }
  debounceHandle = setTimeout(async () => {
    if (!props.park) return
    searchResults.value = await searchCharges(props.park.id, q)
  }, 250)
})

const searchActive = computed(() => searchFocused.value || !!props.search)
const showDropdown = computed(() => searchFocused.value && props.search.length > 0)

function onSearchBlur() {
  setTimeout(() => { searchFocused.value = false }, 100)
}

function clearSearch() {
  emit('update:search', '')
  searchInputEl.value?.focus()
}

function selectResult(type, item) {
  searchFocused.value = false
  // TODO: abrir el panel de detalle del inquilino correspondiente — pendiente hasta construir ese panel
  console.log('Resultado seleccionado:', type, item)
}

defineExpose({ reloadCounts: loadCounts })
</script>

<style scoped>
.cbz-topbar {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.5rem;
  background: var(--side-panel);
  border-left: 1px solid var(--border-subtle);
  gap: 0.75rem;
  height: 56px;
  box-sizing: border-box;
}

.cbz-topbar__status { font-size: 0.85rem; color: var(--text-secondary); }
.cbz-topbar__divider { width: 1px; align-self: stretch; background: rgba(255, 255, 255, 0.15); flex-shrink: 0; }
.cbz-topbar__spacer { flex: 1; }

.cbz-topbar__left { display: flex; flex-direction: column; flex-shrink: 0; }
.cbz-topbar__section { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #ffffff; }
.cbz-topbar__park-name { font-family: var(--font-body); font-size: 11px; color: rgba(255, 255, 255, 0.6); }

.cbz-topbar__period-group { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
.cbz-topbar__period-wrap { position: relative; }

.cbz-topbar__period-select {
  height: 32px; background: #24487a; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 6px;
  padding: 0 0.6rem; color: #ffffff; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; gap: 0.2rem;
}

.cbz-topbar__period-menu {
  position: absolute; top: calc(100% + 6px); left: 0; background: #ffffff; border: 1px solid #e2e8f0;
  border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15); padding: 0.3rem; z-index: 200; max-height: 260px; overflow-y: auto; min-width: 160px;
}
.cbz-topbar__period-menu::-webkit-scrollbar { width: 6px; }
.cbz-topbar__period-menu::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cbz-topbar__period-menu::-webkit-scrollbar-track { background: transparent; }
.cbz-topbar__period-item { display: block; width: 100%; text-align: left; background: none; border: none; border-radius: 5px; padding: 0.35rem 0.5rem; font-family: var(--font-body); font-size: 12px; color: var(--text-content); cursor: pointer; }
.cbz-topbar__period-item:hover { background: #f8fafc; }
.cbz-topbar__period-item--active { background: #eff6ff; color: #3b82f6; font-weight: 500; }

.cbz-topbar__accumulated { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 11px; color: rgba(255, 255, 255, 0.6); cursor: pointer; }

.cbz-topbar__tabs { display: flex; align-items: center; gap: 0.15rem; flex-shrink: 0; }
.cbz-topbar__tab { display: flex; align-items: center; gap: 0.25rem; background: transparent; border: none; border-radius: 20px; padding: 0.3rem 0.6rem; cursor: pointer; font-family: var(--font-body); font-size: 12px; color: rgba(255, 255, 255, 0.6); position: relative; }
.cbz-topbar__tab--active { background: rgba(255, 255, 255, 0.15); color: #ffffff; }

.cbz-topbar__tab--vencidos { background: #fee2e2; color: #e84545; }
.cbz-topbar__tab--vencidos.cbz-topbar__tab--active { background: #fecaca; }

.cbz-topbar__tab-count--pendientes { color: #f0a500; }
.cbz-topbar__tab-count--alCorriente { color: #10b981; }
.cbz-topbar__tab-count--vencidos { color: #e84545; font-weight: 600; }

.cbz-topbar__tab-blink { width: 6px; height: 6px; border-radius: 50%; background: #e84545; animation: cbz-blink 1.4s ease-in-out infinite; }
@keyframes cbz-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

.cbz-topbar__search-wrap { position: relative; flex: 1 1 160px; min-width: 130px; max-width: 200px; }
.cbz-topbar__search { display: flex; align-items: center; height: 32px; background: #24487a; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 6px; padding: 0 0.5rem; gap: 0.35rem; }
.cbz-topbar__search--active { border-color: rgba(255, 255, 255, 0.6); }
.cbz-topbar__search-icon { color: rgba(255, 255, 255, 0.5); font-size: 0.9rem; }
.cbz-topbar__search-input { flex: 1; background: transparent; border: none; outline: none; color: #ffffff; font-family: var(--font-body); font-size: 12px; min-width: 0; }
.cbz-topbar__search-input::placeholder { color: rgba(255, 255, 255, 0.4); }
.cbz-topbar__search-clear { background: none; border: none; color: rgba(255, 255, 255, 0.5); cursor: pointer; display: flex; }

.cbz-topbar__dropdown { position: absolute; top: calc(100% + 6px); left: 0; right: 0; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15); max-height: 320px; overflow-y: auto; z-index: 200; min-width: 260px; }
.cbz-topbar__dropdown::-webkit-scrollbar { width: 6px; }
.cbz-topbar__dropdown::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cbz-topbar__dropdown::-webkit-scrollbar-track { background: transparent; }
.cbz-topbar__dropdown-group + .cbz-topbar__dropdown-group { border-top: 1px solid #e2e8f0; }
.cbz-topbar__dropdown-label { padding: 0.4rem 0.6rem 0.2rem; font-family: var(--font-body); font-size: 0.64rem; font-weight: 500; text-transform: uppercase; color: #94a3b8; }
.cbz-topbar__dropdown-item { display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: 0.35rem 0.6rem; background: transparent; border: none; cursor: pointer; text-align: left; }
.cbz-topbar__dropdown-item:hover { background: #f8fafc; }
.cbz-topbar__dropdown-item-name { font-family: var(--font-body); font-size: 0.8rem; font-weight: 500; color: #1e3a5f; }
.cbz-topbar__dropdown-item-context { font-family: var(--font-body); font-size: 0.7rem; color: #64748b; }
.cbz-topbar__dropdown-empty { padding: 0.6rem; font-size: 0.76rem; color: #64748b; text-align: center; }

.cbz-topbar__new-charge-wrap { position: relative; flex-shrink: 0; }
.cbz-topbar__new-charge-btn { height: 32px; background: #24487a; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; padding: 0 0.65rem; color: #ffffff; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }

.cbz-topbar__statement-wrap { position: relative; flex-shrink: 0; }
.cbz-topbar__statement-btn { height: 32px; background: #ffffff; border: none; border-radius: 6px; padding: 0 0.75rem; color: #1e3a5f; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; transition: background 100ms ease; white-space: nowrap; }
.cbz-topbar__statement-btn:hover { background: #f1f5f9; }

.cbz-topbar__tooltip { position: absolute; top: calc(100% + 6px); left: 0; background: #1e3a5f; color: #ffffff; font-family: var(--font-body); font-size: 11px; padding: 0.5rem 0.65rem; border-radius: 8px; min-width: 220px; z-index: 200; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25); }
.cbz-topbar__tooltip--right { left: auto; right: 0; }

.cbz-topbar__notif { position: relative; background: none; border: none; color: rgba(255, 255, 255, 0.6); font-size: 1.1rem; cursor: pointer; display: flex; flex-shrink: 0; }
.cbz-topbar__notif-badge { position: absolute; top: -6px; right: -8px; background: var(--accent-blue); color: white; font-size: 0.6rem; font-family: var(--font-mono); padding: 0 4px; border-radius: 8px; min-width: 14px; text-align: center; }
.cbz-statusbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1.5rem;
  background: transparent;
  border-bottom: 1px solid var(--border-subtle);
}

.cbz-statusbar__tab {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: transparent;
  border: none;
  border-radius: 20px;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  position: relative;
}

.cbz-statusbar__tab:hover {
  background: rgba(0, 0, 0, 0.03);
}

.cbz-statusbar__tab--active {
  background: #eff6ff;
  color: #1e3a5f;
  font-weight: 500;
}

.cbz-statusbar__tab--vencidos {
  background: #fee2e2;
  color: #dc2626;
}

.cbz-statusbar__tab--vencidos.cbz-statusbar__tab--active {
  background: #fecaca;
}

.cbz-statusbar__tab-count--pendientes { color: #d97706; }
.cbz-statusbar__tab-count--alCorriente { color: #16a34a; }
.cbz-statusbar__tab-count--vencidos { color: #dc2626; font-weight: 600; }

.cbz-statusbar__tab-blink {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dc2626;
  animation: cbz-blink 1.4s ease-in-out infinite;
}

@keyframes cbz-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.cbz-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  background: transparent;
  border-bottom: 1px solid var(--border-subtle);
}

.cbz-statusbar__tabs {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.cbz-statusbar__new-charge-wrap {
  position: relative;
  flex-shrink: 0;
}

.cbz-statusbar__new-charge-btn {
  height: 28px;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 6px;
  padding: 0 0.6rem;
  color: #1e3a5f;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.cbz-statusbar__new-charge-btn:hover {
  background: #f1f5f9;
}

.cbz-statusbar__tooltip {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #1e3a5f;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 11px;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  min-width: 220px;
  z-index: 200;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}
</style>